import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';

export function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) throw error;
      setSuccess('Sign up successful! Please check your email to confirm your account.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      console.error('Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh" bgcolor="#2563eb" gap={8}>
      <Card sx={{ width: 380, boxShadow: 6, borderRadius: 3, bgcolor: '#fff' }}>
        <CardHeader
          title={
            <Typography 
              variant="h4" 
              fontWeight={800} 
              sx={{ 
                background: 'linear-gradient(45deg, #2563eb, #4f46e5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 0.5
              }}
            >
              Sign Up
            </Typography>
          }
          subheader={
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                fontSize: '1rem',
                opacity: 0.8,
                letterSpacing: '0.01em'
              }}
            >
              Create a new account
            </Typography>
          }
        />
        <form onSubmit={handleSignUp}>
          <CardContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                id="email"
                label="Email"
                type="email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500,
                  }
                }}
              />
              <TextField
                id="password"
                label="Password"
                type="password"
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500,
                  }
                }}
              />
              {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ borderRadius: 2 }}>{success}</Alert>}
            </Box>
          </CardContent>
          <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 2, pb: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth 
              disabled={loading} 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1rem', 
                py: 1.2,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #2563eb, #4f46e5)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1d4ed8, #4338ca)',
                }
              }}
            >
              {loading ? 'Signing up...' : 'Sign Up'}
            </Button>
            <Button 
              href="/login" 
              color="secondary" 
              fullWidth 
              sx={{ 
                mt: 1, 
                fontWeight: 500,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
            >
              Already have an account? Login
            </Button>
          </CardActions>
        </form>
      </Card>
      <Box sx={{ 
        width: 420, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'flex-start', 
        color: '#fff',
        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Typography 
          variant="h2" 
          fontWeight={800} 
          sx={{ 
            lineHeight: 1.1, 
            mb: 2,
            background: 'linear-gradient(45deg, #ffffff, #e2e8f0)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            letterSpacing: '-0.02em'
          }}
        >
          INTJ App <br />You are not alone, we are a community.
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            opacity: 0.9,
            letterSpacing: '0.01em',
            lineHeight: 1.6,
            fontWeight: 500
          }}
        >
          Join us today! Create your account and unlock all the features. Fast, secure, and easy registration.
        </Typography>
      </Box>
    </Box>
  );
} 